import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import {
  CheckCircle, XCircle, Clock, Calendar, Phone,
  Loader2, ExternalLink, MessageCircle, ChevronDown,
} from 'lucide-react';
import { useBookingApprovals } from '../hooks/booking/useBookingApprovals';
import { getPublicWebBaseUrl } from '../utils/publicWebBaseUrl';
import { Button, EmptyState, InfoBanner, StatusPill, cn } from '../components/ui';
import { composeSlotLabel } from '../utils/slotName';

const FILTER_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

// ─── main page ────────────────────────────────────────────────────────────────

const BookingApprovals = () => {
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [approvalModal, setApprovalModal] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState(null);

  const {
    requests,
    loading,
    error,
    refresh: loadRequests,
    approveRequest,
    rejectRequest,
  } = useBookingApprovals({ filter });

  const handleApprove = async (id, paymentInfo) => {
    if (!paymentInfo) {
      // First click — open the approval form
      setApprovalModal(id);
      return;
    }

    setActionLoading(id);
    try {
      const data = await approveRequest(id, paymentInfo);
      if (data?.data?.whatsappLink) {
        setWhatsappLink(data.data.whatsappLink);
      }
      setApprovalModal(null);
    } catch (e) {
      // error surfaced via hook's error state
      console.error('Approve failed:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(id);
    try {
      await rejectRequest(id, reason);
    } catch (e) {
      console.error('Reject failed:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const facilityId = localStorage.getItem('active_facility_id');
  const currentUrl = getPublicWebBaseUrl();

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ga-text-primary">Booking Requests</h1>
            <p className="mt-1 text-sm text-ga-text-muted">
              Approve or reject booking requests from customers
            </p>
          </div>
          <ShareableLink facilityId={facilityId} baseUrl={currentUrl} />
        </div>

        {/* Filter tabs */}
        <div className="mb-5 flex flex-wrap gap-2">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setFilter(tab.value)}
              className={cn(
                'rounded-ga-full border px-4 py-1.5 text-sm font-semibold transition-colors',
                filter === tab.value
                  ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                  : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-text-primary'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* WhatsApp confirmation banner */}
        {whatsappLink && (
          <InfoBanner
            variant="success"
            title="Booking approved!"
            onDismiss={() => setWhatsappLink(null)}
            className="mb-4"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span>Send confirmation via WhatsApp:</span>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-ga-sm bg-ga-success px-3 py-1.5 text-xs font-semibold text-ga-text-on-primary hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" /> Send on WhatsApp
              </a>
            </div>
          </InfoBanner>
        )}

        {error && (
          <InfoBanner variant="danger" title={error} className="mb-4">
            <button
              type="button"
              onClick={loadRequests}
              className="mt-1 rounded-ga-sm bg-ga-danger px-3 py-1 text-xs font-bold text-ga-text-on-primary hover:opacity-90"
            >
              Retry
            </button>
          </InfoBanner>
        )}

        {/* Request list */}
        {loading ? (
          <div className="py-12 text-center text-ga-text-muted">
            <Loader2 size={32} className="mx-auto mb-2 block animate-spin" />
            Loading requests...
          </div>
        ) : requests.length === 0 ? (
          <EmptyState
            card
            icon={Clock}
            title="No booking requests"
            description={`No ${filter || ''} booking requests found.`}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {requests.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                fmtDate={fmtDate}
                actionLoading={actionLoading}
                approvalModal={approvalModal}
                onApprove={handleApprove}
                onReject={handleReject}
                onCancelModal={() => setApprovalModal(null)}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ─── RequestCard ──────────────────────────────────────────────────────────────

function RequestCard({ request: r, fmtDate, actionLoading, approvalModal, onApprove, onReject, onCancelModal }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // Controlled approval form state
  const [totalAmount, setTotalAmount] = useState(r.price != null ? String(r.price) : '0');
  const [advanceAmount, setAdvanceAmount] = useState(r.advance_amount != null ? String(r.advance_amount) : '0');
  const [paymentMode, setPaymentMode] = useState('cash');

  const isLoading = actionLoading === r.id;
  const showApprovalForm = approvalModal === r.id;

  const slotLabel = composeSlotLabel(r.slot_name, r.time_slot);

  const handleApproveClick = (e) => {
    e.stopPropagation();
    if (showApprovalForm) {
      // Confirm with collected values
      onApprove(r.id, {
        totalAmount: parseFloat(totalAmount) || 0,
        advanceAmount: parseFloat(advanceAmount) || 0,
        paymentMode,
      });
    } else {
      onApprove(r.id, null); // opens form
    }
  };

  const handleRejectClick = (e) => {
    e.stopPropagation();
    if (showRejectInput) {
      onReject(r.id, rejectReason);
      setShowRejectInput(false);
      setRejectReason('');
    } else {
      setShowRejectInput(true);
    }
  };

  return (
    <div className={cn(
      'relative overflow-hidden rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft',
      'before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-ga-primary before:content-[""]'
    )}>
      {/* Header row */}
      <div
        className="flex cursor-pointer items-start justify-between gap-3 p-4 pl-5"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-bold text-ga-text-primary">
              {r.team_a_name || r.contact_name || 'Unnamed'}
            </span>
            <StatusPill status={r.status} size="sm" />
            <span className="text-xs text-ga-text-subtle">{r.reference}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ga-text-muted">
            <span className="flex items-center gap-1">
              <Calendar size={14} /> {fmtDate(r.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} /> {slotLabel === 'N/A' ? 'Any slot' : slotLabel}
            </span>
            {r.contact_phone && (
              <a
                href={`tel:${r.contact_phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 font-medium text-ga-primary hover:underline"
              >
                <Phone size={14} /> {r.contact_phone}
              </a>
            )}
            {r.service_name && <span className="flex items-center gap-1">{r.service_name}</span>}
          </div>
        </div>
        <ChevronDown
          size={18}
          className={cn(
            'mt-1 shrink-0 text-ga-text-subtle transition-transform duration-200',
            expanded && 'rotate-180'
          )}
        />
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-ga-border-subtle px-5 pb-4 pt-3">
          <div className="grid grid-cols-2 gap-2 text-[13px]">
            <Detail label="Ground" value={r.ground_name} />
            <Detail label="Service" value={r.service_name} />
            <Detail label="Booking Type" value={r.booking_type} />
            <Detail label="Submitted" value={fmtDate(r.created_at)} />
            {r.team_a_captain_name && <Detail label="Team A Captain" value={`${r.team_a_captain_name} (${r.team_a_captain_phone || '—'})`} />}
            {r.team_b_name && <Detail label="Team B" value={`${r.team_b_name} — ${r.team_b_captain_name || ''}`} />}
            {r.special_requests && <Detail label="Special Requests" value={r.special_requests} span />}
            {r.rejection_reason && <Detail label="Rejection Reason" value={r.rejection_reason} span />}
          </div>

          {/* Controlled approval form */}
          {showApprovalForm && r.status === 'pending' && (
            <div className="mt-3 grid grid-cols-1 items-end gap-2 rounded-ga-sm bg-ga-primary-soft p-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-ga-text-muted">
                  Total Amount (₹)
                </label>
                <input
                  type="number"
                  step="any"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="ga-input"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-ga-text-muted">
                  Advance (₹)
                </label>
                <input
                  type="number"
                  step="any"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="ga-input"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-ga-text-muted">
                  Payment Mode
                </label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="ga-input"
                >
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="upi">UPI</option>
                </select>
              </div>
            </div>
          )}

          {/* Reject reason input */}
          {showRejectInput && r.status === 'pending' && (
            <div className="mt-3">
              <input
                type="text"
                placeholder="Reason for rejection (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="ga-input"
              />
            </div>
          )}

          {/* Action buttons */}
          {r.status === 'pending' && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={handleApproveClick}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                {showApprovalForm ? 'Confirm Approval' : 'Approve'}
              </Button>
              {showApprovalForm && (
                <Button
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); onCancelModal(); }}
                >
                  Cancel
                </Button>
              )}
              {!showApprovalForm && (
                <Button
                  variant="ghost"
                  onClick={handleRejectClick}
                  disabled={isLoading}
                  className="flex-1 border-ga-danger/40 text-ga-danger hover:bg-ga-danger/10"
                >
                  <XCircle size={16} /> {showRejectInput ? 'Confirm Deny' : 'Deny'}
                </Button>
              )}
              {r.contact_phone && (
                <Button
                  as="a"
                  variant="ghost"
                  href={`tel:${r.contact_phone}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone size={16} /> Call
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Detail cell ──────────────────────────────────────────────────────────────

function Detail({ label, value, span }) {
  return (
    <div className={span ? 'col-span-2' : undefined}>
      <span className="text-[11px] font-semibold text-ga-text-subtle">{label}</span>
      <div className="mt-0.5 text-[13px] text-ga-text-primary">{value || '—'}</div>
    </div>
  );
}

// ─── ShareableLink ────────────────────────────────────────────────────────────

function ShareableLink({ facilityId, baseUrl }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [serviceId, setServiceId] = useState('');

  if (!facilityId) return null;

  const handleCopy = () => {
    if (!serviceId) return;
    const link = `${baseUrl}/book/${facilityId}/${serviceId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-ga-sm border border-ga-primary bg-ga-primary-soft px-3.5 py-2 text-sm font-semibold text-ga-primary transition-colors hover:bg-ga-primary/20"
      >
        <ExternalLink size={14} /> Share Booking Link
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-2 min-w-[280px] rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface p-3 shadow-ga-soft">
          <p className="mb-2 text-xs text-ga-text-muted">
            Enter a service ID to generate a shareable booking link:
          </p>
          <div className="flex gap-1.5">
            <input
              type="number"
              placeholder="Service ID"
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="ga-input flex-1"
            />
            <button
              type="button"
              onClick={handleCopy}
              disabled={!serviceId}
              className="ga-btn-primary shrink-0 px-3 py-2 text-sm"
            >
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingApprovals;
